/**
 * Word Wagon — what may be written on the sign, and what may fall under it (specs/wagon-mode.md
 * §6). Phaser-free, so the one rule this mode cannot get wrong is checked by a test that walks
 * every lesson in every language rather than by the one lesson a developer happened to open.
 *
 * The rule: **a word that is a target in ANY sense must never fall as a hazard.** Get it wrong and
 * the game wrecks a wagon for a correct read, which is worse than any amount of difficulty. It is
 * inherited rather than invented — `wordInClass` resolves the sub-class case (a creature counts on
 * a "Nomen" wagon) and `canBeDecoy` applies the class's own `decoyExcludeClasses`, exactly as
 * BoardScene and SurvivalScene do.
 */
import { WAGON } from '@/game/config';
import { wordInClass, canBeDecoy } from '@/data/packs/membership';
import { DRILL_CLASSES } from '@/game/progression/config';
import type { Pack, PackClass, PackWord } from '@/data/packs/types';
import type { WagonWord } from './track';

export interface CargoPools {
  /** Words of the class on the sign. */
  targets: WagonWord[];
  /** Words that are not, and that may safely be dropped on the player's head. */
  hazards: WagonWord[];
}

/** The class as the sign should read it — synthesized from the bare id for a pack with none. */
export function classFor(classes: readonly PackClass[], id: string): PackClass {
  return classes.find((c) => c.id === id) ?? { id, label: id, prompt: id };
}

export function poolsFor(
  words: readonly PackWord[],
  classes: readonly PackClass[],
  id: string,
): CargoPools {
  const cls = classFor(classes, id);
  const say = (w: PackWord, target: boolean): WagonWord => ({ id: w.id ?? w.text, text: w.text, target });
  return {
    targets: words.filter((w) => wordInClass(w, id)).slice(0, WAGON.workingSetSize).map((w) => say(w, true)),
    hazards: words.filter((w) => canBeDecoy(w, cls)).slice(0, WAGON.workingSetSize).map((w) => say(w, false)),
  };
}

/** Every class a pack could put on a sign: its drill classes, plus whatever else it declares. */
export function candidateClassIds(pack: Pack): string[] {
  const drills = (pack.drillClasses && pack.drillClasses.length > 0) ? pack.drillClasses : DRILL_CLASSES;
  return [...new Set([...drills, ...(pack.classes ?? []).map((c) => c.id)])];
}

/**
 * The classes that can actually carry a wagon: enough cargo to fill one several times over, and
 * enough hazards that dodging is a real task rather than a coincidence.
 *
 * Never empty for a pack with any words at all — a run must not open on an empty rail — so a pack
 * too thin for the floors falls back to whatever it has the most of, and the rung is thin rather
 * than absent.
 */
export function eligibleClassIds(words: readonly PackWord[], pack: Pack): string[] {
  const classes = pack.classes ?? [];
  const candidates = candidateClassIds(pack);
  const eligible = candidates.filter((id) => {
    const { targets, hazards } = poolsFor(words, classes, id);
    return targets.length >= WAGON.minTargets && hazards.length >= WAGON.minHazards;
  });
  if (eligible.length > 0) return eligible;

  const best = candidates
    .map((id) => ({ id, n: poolsFor(words, classes, id).targets.length }))
    .sort((a, b) => b.n - a.n)[0];
  return best && best.n > 0 ? [best.id] : [];
}
